import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { OERadioOptions } from '../../../../../../global/oe-radio-options/oe-radio-options';
import { ICustomFieldsDTO } from '../../../../../../../api/custom-fields.dao';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../global/text/text';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import { FieldToggle } from './fields/custom-field-default';
import CustomFieldVM, { CustomFieldType } from './custom-field.viewmodel';
import { cancelText } from '../../../../../../../constants/common-labels.constants';

interface IProps {
    vm: CustomFieldVM;
    fields: ICustomFieldsDTO[];
    newField: ICustomFieldsDTO;
    isOpen: boolean;
    intl: IntlShape;
    isValidField: (field: ICustomFieldsDTO) => boolean;
    getShortPlainValidationError: (text: string) => string | null;
    getShortRichTextValidationError: (text: string) => string | null;
    updateOrder: (fields: ICustomFieldsDTO[]) => void;
    toggleAddForm: (value: boolean) => void;
    cleanNewForm: () => void;
    combineOptionsArrayIntoString: (options: string[]) => string;
    getOptionsArray: (text: string) => string[];
    showValidationErrors: (text: string, isMandatory?: boolean, isAlphaNumeric?: boolean) => string;
    isExternalFieldIDChanged: boolean;
    isLabelChanged: boolean;
    setTouchFieldStatus: (fieldName: string) => void;
}

export const CustomRadioValues = {
    optional: 'Optional',
    required: 'Required'
};

const CustomFieldCreateForm = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        fields,
        newField,
        isOpen,
        intl,
        isValidField,
        toggleAddForm,
        cleanNewForm,
        getOptionsArray,
        getShortRichTextValidationError,
        combineOptionsArrayIntoString,
        showValidationErrors,
        isExternalFieldIDChanged,
        isLabelChanged,
        setTouchFieldStatus,
        vm
    } = props;

    const externalFieldIdError = showValidationErrors(newField.externalFieldId, true, true);
    const labelFieldError = showValidationErrors(newField.label, true);
    const descriptionFieldError = showValidationErrors(newField.description);
    const isFormValid = isValidField(newField);

    return (<>
        {isOpen &&
            <div className='website-custom__popover'>
                <CommonPopupBodyFooterLayout
                    body={
                        <form className='oe-form'>
                            <div className='oe-form__body'>
                                <label className='m-bottom-15px website-custom__title'>
                                    <FormattedMessage
                                        id='oe.common.new'
                                        defaultMessage='New {prefix}'
                                        description='Button text'
                                        values={{
                                            prefix: 'Custom Field'
                                        }}
                                    />
                                </label>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.customField.fieldType'
                                        defaultMessage='Field Type'
                                        description='Label text'
                                    />}
                                >
                                    <select
                                        value={newField.type}
                                        onChange={mobx.action(e => {
                                            const { value } = e.target;
                                            if (value === CustomFieldType.text) newField.optionsList = [];
                                            newField.type = value as CustomFieldType;
                                        })}
                                        className='oe-form-field__control'
                                    >
                                        <option value={CustomFieldType.text}>{intl.formatMessage({
                                            id: 'oe.customField.freeText',
                                            defaultMessage: 'Free text',
                                            description: 'Option label'
                                        })}</option>
                                        <option value={CustomFieldType.dropdown}>{intl.formatMessage({
                                            id: 'oe.customField.singleSelectDropDownList',
                                            defaultMessage: 'Single-select drop-down list',
                                            description: 'Option label'
                                        })}</option>
                                        <option value={CustomFieldType.checkbox}>{intl.formatMessage({
                                            id: 'oe.customField.checkbox',
                                            defaultMessage: 'Checkbox',
                                            description: 'Option label'
                                        })}</option>
                                        <option value={CustomFieldType.textarea}>{intl.formatMessage({
                                            id: 'oe.customField.textBox',
                                            defaultMessage: 'Text Box',
                                            description: 'Option label'
                                        })}</option>
                                    </select>
                                </FormField>
                                { newField.type === CustomFieldType.dropdown && <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.customField.optionsList'
                                        defaultMessage='List Options'
                                        description='Label text'
                                    />}
                                >
                                    <textarea
                                        className='oe-form-field__control'
                                        value={combineOptionsArrayIntoString(newField.optionsList)}
                                        onChange={mobx.action(e => newField.optionsList = getOptionsArray(e.target.value))}
                                    />
                                    <p className='oe-form-field__info'>
                                        <FormattedMessage
                                            id='oe.customField.enterEachListOptionOnANewLineWithoutEmpty'
                                            description='Input info text'
                                            defaultMessage='Enter each list option on a new line, at least one option should be added'
                                        />
                                    </p>
                                </FormField>}
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.label.fieldId'
                                        description='Label text'
                                        defaultMessage='Field ID'
                                    />}
                                >
                                    <div className='flex'>
                                        <input
                                            id='custom-fieldID-label'
                                            className='oe-form-field__control'
                                            value={newField.externalFieldId}
                                            onBlur={() => setTouchFieldStatus('isExternalFieldIDChanged')}
                                            onChange={mobx.action(e => {
                                                setTouchFieldStatus('isExternalFieldIDChanged');
                                                newField.externalFieldId = e.target.value;
                                            })}
                                            type='text'
                                        />
                                    </div>
                                    {isExternalFieldIDChanged && externalFieldIdError &&
                                        <span className='oe-form-field__error'>
                                            {<FormattedMessage
                                                id='oe.label.fieldIdError'
                                                description='Label text'
                                                defaultMessage='Field ID {suffix}'
                                                values={{
                                                    suffix: externalFieldIdError
                                                }}
                                            />}
                                        </span>
                                    }
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.label.fieldLabel'
                                        description='Label text'
                                        defaultMessage='Field Label'
                                    />}
                                >
                                    { newField.type === CustomFieldType.checkbox
                                    ? (
                                        <RichTextEditor
                                            value={newField.label}
                                            onChange={mobx.action((_, editor) => {
                                                newField.label = editor.getData();
                                                setTouchFieldStatus('isLabelChanged');
                                            })}
                                            onBlur={() => setTouchFieldStatus('isLabelChanged')}
                                            className='custom-field-checkbox-label'
                                            validationError={isLabelChanged && getShortRichTextValidationError(newField.label)}
                                        />
                                        ) : (
                                        <>
                                            <input
                                                id='customField-label'
                                                className='oe-form-field__control'
                                                value={newField.label}
                                                onBlur={() => setTouchFieldStatus('isLabelChanged')}
                                                onChange={mobx.action(e => {
                                                    setTouchFieldStatus('isLabelChanged');
                                                    newField.label = e.target.value;
                                                })}
                                                type='text'
                                            />
                                            {isLabelChanged && labelFieldError &&
                                            <span className='oe-form-field__error'>
                                                 {<FormattedMessage
                                                     id='oe.label.fieldLabelError'
                                                     description='Label text'
                                                     defaultMessage='Field Label {suffix}'
                                                     values={{
                                                         suffix: labelFieldError
                                                     }}
                                                 />}
                                            </span>
                                            }
                                        </>
                                    )}
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.label.fieldDescription'
                                        description='Field label text'
                                        defaultMessage='Description'
                                    />}>
                                    <textarea
                                        className='oe-form-field__control'
                                        value={newField.description}
                                        onChange={mobx.action(e => newField.description = e.target.value)}
                                    />
                                    {descriptionFieldError &&
                                    <span className='oe-form-field__error'>
                                      {<FormattedMessage
                                          id='oe.label.fieldDescriptionError'
                                          description='Label text'
                                          defaultMessage='Description {suffix}'
                                          values={{
                                              suffix: descriptionFieldError
                                          }}
                                      />}
                                    </span>
                                    }
                                </FormField>
                                {!vm.isTriagedNoSelectionForm && <FormField className='m-bottom-20px'>
                                    <OERadioOptions
                                        options={[
                                            {
                                                value: 'false',
                                                label: CustomRadioValues.optional
                                            },
                                            {
                                                value: 'true',
                                                label: CustomRadioValues.required
                                            }
                                        ]}
                                        value={`${vm.isFieldMandatory(newField)}`}
                                        onOptionToggle={mobx.action(value => vm.setIsFieldMandatory(newField, JSON.parse(value)))}
                                        disabled={false}
                                        styles='website-custom__radio'
                                    />
                                </FormField>}
                                <FormField className='m-bottom-20px'>
                                    <div className='website-custom__toggle'>
                                        <OEToggle
                                            isSelected={vm.isFieldVisible(newField)}
                                            onClick={() => mobx.runInAction(() => {
                                                if (vm.isFieldVisible(newField)) {
                                                    vm.setFieldAsHidden(newField);
                                                } else {
                                                    vm.setFieldAsVisible(newField);
                                                }
                                            })}
                                        />
                                        <Text
                                            message={vm.isFieldVisible(newField) ? FieldToggle.displayed : 'Field' + FieldToggle.hidden} />
                                    </div>
                                </FormField>
                            </div>
                        </form>}
                    footer={
                        <>
                            <div className='buttons-block buttons-block--from-right'>
                                <DefaultButton
                                    text={<FormattedMessage
                                        id='oe.website.addToForm'
                                        description='Button text'
                                        defaultMessage='Add to Form'
                                    />
                                    }
                                    disabled={!isFormValid}
                                    modifier={['slim', 'confirm-safe']}
                                    onClick={() => {
                                        mobx.runInAction(() => {
                                            // Temporary unique id
                                            const id = new Date();

                                            vm.updateOrderForAllFieldConfigs(fields.concat({
                                                ...newField,
                                                label: newField.label.trim(),
                                                description: newField.description.trim(),
                                                externalFieldId: newField.externalFieldId.trim(),
                                                customFieldId: -id.getTime(),
                                                canBeHidden: true
                                            }));
                                            toggleAddForm(false);
                                            cleanNewForm();
                                        });
                                    }}
                                />
                                <DefaultCloseSubWindowButton
                                    modifier={['slim']}
                                    onClick={() => {
                                        toggleAddForm(false);
                                        return false;
                                    }}
                                    text={cancelText()}
                                />
                            </div>
                        </>
                    }
                />
            </div>
        }
    </>);
}));

export { CustomFieldCreateForm };
